import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowLeft, ArrowRight, Check, Briefcase } from "lucide-react";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import BackToDemoButton from "../components/BackToDemoButton";

const AssessmentPage = () => {
  const navigate = useNavigate();
  const [currentStep, setCurrentStep] = useState(1);
  const [answers, setAnswers] = useState({});
  const [textInput, setTextInput] = useState("");

  // Base questions that determine the path
  const baseQuestions = [
    {
      id: 1,
      question: "What industry does your business operate in?",
      type: "select",
      options: [
        "E-commerce & Retail",
        "Healthcare & Medical",
        "Financial Services & Banking",
        "Technology & Software",
        "Manufacturing & Supply Chain",
        "Marketing & Advertising",
        "Education & Training",
        "Real Estate",
        "Legal Services",
        "Hospitality & Tourism",
        "Other"
      ]
    },
    {
      id: 2,
      question: "What's your company size?",
      type: "select",
      options: [
        "Solo entrepreneur (just me)",
        "2-10 employees",
        "11-50 employees",
        "51-200 employees",
        "201-1000 employees",
        "1000+ employees"
      ]
    },
    {
      id: 3,
      question: "What's the primary business challenge you're looking to solve?",
      type: "select",
      options: [
        "Customer Support & Service",
        "Marketing & Lead Generation",
        "Sales & CRM",
        "Data Analysis & Business Intelligence",
        "Content Creation & Management",
        "Operations & Workflow Automation",
        "Product Development",
        "Human Resources & Recruiting",
        "Financial Management & Accounting"
      ]
    }
  ];

  // Industry-specific follow-up questions
  const industrySpecificQuestions = {
    "E-commerce & Retail": [
      {
        id: 4,
        question: "What's your average monthly order volume?",
        type: "select",
        options: [
          "Less than 100 orders",
          "100-500 orders",
          "500-2,000 orders",
          "2,000-10,000 orders",
          "10,000+ orders"
        ]
      },
      {
        id: 5,
        question: "Which e-commerce channels are you currently using?",
        type: "select",
        options: [
          "Own website only",
          "Marketplace (Amazon, eBay, etc.)",
          "Social media (Instagram, Facebook)",
          "Multiple channels",
          "Planning to launch"
        ]
      },
      {
        id: 6,
        question: "What's your biggest pain point in customer experience?",
        type: "select",
        options: [
          "Response time to customer inquiries",
          "Product recommendations & personalization",
          "Cart abandonment",
          "Returns & refunds management",
          "Inventory management"
        ]
      }
    ],
    "Healthcare & Medical": [
      {
        id: 4,
        question: "What type of healthcare service do you provide?",
        type: "select",
        options: [
          "Hospital/Clinic",
          "Telemedicine",
          "Mental Health Services",
          "Dental Practice",
          "Medical Research",
          "Healthcare Administration"
        ]
      },
      {
        id: 5,
        question: "What's your primary operational challenge?",
        type: "select",
        options: [
          "Patient scheduling & management",
          "Medical records & documentation",
          "Billing & insurance claims",
          "Patient communication",
          "Diagnostic support",
          "Staff coordination"
        ]
      },
      {
        id: 6,
        question: "How many patients do you serve monthly?",
        type: "select",
        options: [
          "Less than 100",
          "100-500",
          "500-2,000",
          "2,000-10,000",
          "10,000+"
        ]
      }
    ],
    "Financial Services & Banking": [
      {
        id: 4,
        question: "What financial services do you offer?",
        type: "select",
        options: [
          "Banking services",
          "Investment management",
          "Insurance",
          "Financial planning & advisory",
          "Payment processing",
          "Lending & credit"
        ]
      },
      {
        id: 5,
        question: "What's your biggest operational challenge?",
        type: "select",
        options: [
          "Fraud detection & prevention",
          "Risk assessment",
          "Customer onboarding & KYC",
          "Regulatory compliance",
          "Customer service automation",
          "Data analysis & insights"
        ]
      },
      {
        id: 6,
        question: "How many clients do you currently serve?",
        type: "select",
        options: [
          "Less than 100",
          "100-1,000",
          "1,000-10,000",
          "10,000-100,000",
          "100,000+"
        ]
      }
    ],
    "default": [
      {
        id: 4,
        question: "How would you describe your current digital maturity?",
        type: "select",
        options: [
          "Just starting - manual processes",
          "Basic - using some digital tools",
          "Intermediate - partially automated",
          "Advanced - mostly automated",
          "Cutting-edge - fully digital"
        ]
      },
      {
        id: 5,
        question: "What's your timeline for implementing AI solutions?",
        type: "select",
        options: [
          "Immediate (within 1 month)",
          "Short-term (1-3 months)",
          "Medium-term (3-6 months)",
          "Long-term (6-12 months)",
          "Exploring options (12+ months)"
        ]
      },
      {
        id: 6,
        question: "What's your monthly budget for AI tools?",
        type: "select",
        options: [
          "Under $100",
          "$100 - $500",
          "$500 - $2,000",
          "$2,000 - $10,000",
          "$10,000 - $50,000",
          "$50,000+"
        ]
      }
    ]
  };

  const finalQuestions = [
    {
      id: 7,
      question: "Describe your specific use case in detail",
      type: "textarea",
      placeholder: "Tell us more about what you're trying to achieve. Be as specific as possible - what problem are you solving, who are your users, what outcomes are you looking for?"
    },
    {
      id: 8,
      question: "What's your expected ROI timeline?",
      type: "select",
      options: [
        "Immediate impact (0-3 months)",
        "Short-term (3-6 months)",
        "Medium-term (6-12 months)",
        "Long-term (12+ months)",
        "Not a primary concern"
      ]
    },
    {
      id: 9,
      question: "Do you have technical resources to implement AI solutions?",
      type: "select",
      options: [
        "Yes - dedicated IT/tech team",
        "Partially - limited technical expertise",
        "No - need fully managed solutions",
        "Planning to hire",
        "Will use external consultants"
      ]
    },
    {
      id: 10,
      question: "What's your email address? (We'll send your personalized recommendations)",
      type: "email",
      placeholder: "your@company.com"
    }
  ];

  // Determine which questions to show based on industry
  const getQuestions = () => {
    const industry = answers[1] || "";
    const industryKey = industrySpecificQuestions[industry] ? industry : "default";
    return [...baseQuestions, ...industrySpecificQuestions[industryKey], ...finalQuestions];
  };

  const questions = getQuestions();
  const totalQuestions = 10; // Fixed total for progress bar

  const handleAnswer = (answer) => {
    const updatedAnswers = { ...answers, [currentStep]: answer };
    setAnswers(updatedAnswers);
    setTextInput("");
    
    if (currentStep < totalQuestions) {
      setCurrentStep(currentStep + 1);
    } else {
      // Assessment complete, navigate to results with all assessment data
      setTimeout(() => {
        const assessmentData = {
          industry: updatedAnswers[1] || "All Industries",
          company_size: updatedAnswers[2] || "",
          challenge: updatedAnswers[3] || "",
          email: updatedAnswers[10] || ""
        };
        
        // Navigate to demo results page with assessment data
        navigate('/results', {
          state: {
            query: `${assessmentData.industry} - ${assessmentData.challenge}`,
            fromAssessment: true,
            assessmentData: assessmentData
          }
        });
      }, 500);
    }
  };

  const handleTextSubmit = () => {
    if (textInput.trim()) {
      handleAnswer(textInput);
    }
  };

  const currentQuestion = questions[currentStep - 1];

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep(currentStep - 1);
      setTextInput(answers[currentStep - 1] || "");
    }
  };

  return (
    <div className="min-h-screen bg-white dark:bg-[#121212]">
      <BackToDemoButton />
      {/* Header */}
      <header className="fixed top-0 left-0 right-0 z-50 bg-white/95 dark:bg-[#121212]/95 backdrop-blur border-b border-gray-200 dark:border-gray-800">
        <div className="max-w-6xl mx-auto px-4 sm:px-8 py-3 flex justify-between items-center h-16">
          <button
            onClick={() => navigate('/')}
            className="flex items-center gap-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100"
          >
            <ArrowLeft size={20} />
            <img src="/logo.svg" alt="AI Fit Engine Logo" className="w-8 h-8" />
            <span className="text-xl sm:text-2xl font-bold">AI Fit Engine</span>
          </button>
          <div className="flex items-center gap-3 sm:gap-6">
            <button
              onClick={() => navigate('/about')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              About
            </button>
            <button
              onClick={() => navigate('/startup/login')}
              className="px-3 sm:px-4 py-2 text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 text-xs sm:text-sm font-medium transition-colors"
            >
              For Startups
            </button>
          </div>
        </div>
      </header>

      {/* Assessment Content */}
      <div className="pt-24 pb-16 max-w-3xl mx-auto px-4 sm:px-8">
        {/* Progress Bar */}
        <div className="mb-12">
          <div className="flex items-center justify-between mb-2">
            <span className="text-sm text-gray-600 dark:text-gray-400">
              Question {currentStep} of {totalQuestions}
            </span>
            <span className="text-sm text-gray-600 dark:text-gray-400">
              {Math.round((currentStep / totalQuestions) * 100)}%
            </span>
          </div>
          <div className="w-full bg-gray-200 dark:bg-gray-700 rounded-full h-2">
            <div
              className="bg-orange-500 h-2 rounded-full transition-all duration-300"
              style={{ width: `${(currentStep / totalQuestions) * 100}%` }}
            ></div>
          </div>
        </div>

        {/* Question */}
        <div className="mb-8">
          <div className="flex items-start gap-3 mb-6">
            <div className="mt-1">
              <Briefcase className="text-orange-500" size={24} />
            </div>
            <h2 className="text-3xl sm:text-4xl font-bold text-gray-900 dark:text-gray-100">
              {currentQuestion.question}
            </h2>
          </div>

          {/* Render based on question type */}
          {currentQuestion.type === "textarea" ? (
            <div className="space-y-4">
              <Textarea
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={currentQuestion.placeholder}
                rows={6}
                className="w-full text-base"
              />
              <Button
                onClick={handleTextSubmit}
                disabled={!textInput.trim()}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-base"
              >
                Continue
                <ArrowRight className="ml-2" size={20} />
              </Button>
            </div>
          ) : currentQuestion.type === "email" ? (
            <div className="space-y-4">
              <Input
                type="email"
                value={textInput}
                onChange={(e) => setTextInput(e.target.value)}
                placeholder={currentQuestion.placeholder}
                className="w-full text-base py-4"
              />
              <Button
                onClick={handleTextSubmit}
                disabled={!textInput.trim() || !textInput.includes("@")}
                className="bg-orange-500 hover:bg-orange-600 text-white px-8 py-3 text-base"
              >
                Get My Recommendations
                <Check className="ml-2" size={20} />
              </Button>
            </div>
          ) : (
            <div className="space-y-4">
              {currentQuestion.options.map((option, index) => (
                <button
                  key={index}
                  onClick={() => handleAnswer(option)}
                  className="w-full text-left p-6 bg-white dark:bg-[#1E1E1E] border-2 border-gray-200 dark:border-gray-700 rounded-xl hover:border-orange-500 dark:hover:border-orange-500 transition-all hover:shadow-lg group"
                >
                  <div className="flex items-center justify-between">
                    <span className="text-lg text-gray-900 dark:text-gray-100 font-medium">
                      {option}
                    </span>
                    <ArrowRight
                      className="text-gray-400 group-hover:text-orange-500 transition-colors"
                      size={20}
                    />
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Back Button */}
        {currentStep > 1 && (
          <button
            onClick={handleBack}
            className="text-gray-600 dark:text-gray-400 hover:text-gray-900 dark:hover:text-gray-100 flex items-center gap-2 mt-6"
          >
            <ArrowLeft size={16} />
            Previous Question
          </button>
        )}
      </div>
    </div>
  );
};

export default AssessmentPage;
